// Type-only, like `./column-edit-kind.ts`: importing `NodeColumns` as a VALUE
// pulls all of `table-store.ts` (and the design system) into anything that
// imports this module, which throws in the test environment. Keys below are
// therefore string literals, matching that module's own Sets.
import type { NodeColumns, TableRow } from '../table-store';

/**
 * ## Why the compiler's enums are NOT imported here
 *
 * They cannot be, from a module the test suite reaches. `@pypestream/agent-compiler`
 * ships one bundled `out/index.js` plus per-module `.d.ts` only — there is no
 * `out/common/constants.js` to import directly, and the package's `exports` map
 * is `"."` alone. Loading that barrel pulls in langium and the diagram server,
 * which throws in a test environment (`createTreeWalker is not a function`
 * under node, `Cannot convert undefined or null to object` under jsdom).
 * Verified both ways.
 *
 * The barrel DOES load in plain Node, so the values below are checked against
 * the real published enums by `scripts/check-behavior-drift.mjs`
 * (`yarn check:behavior-drift`) rather than by a unit test. That script is the
 * drift guard; treat the literals here as needing its blessing.
 *
 * Importing the barrel here would also tax every test that transitively imports
 * this module — including the property panel's — with a `vi.mock`.
 */

/**
 * What kind of surface a column's value should be edited with.
 *
 * Richer than `ColumnEditKind` in `./column-edit-kind.ts` on purpose, and
 * SEPARATE from it. That module is shared with the cell renderer and the
 * store's edit-entry methods on the table's hot path, and a unit test keeps its
 * SELECT set in lockstep with `cell.tsx`'s three branches — so it is not the
 * place to add editor variants that only the property panel understands. This
 * module is panel-only; the grid is untouched.
 */
export type PropertyEditorKind =
  | 'readonly'
  | 'checkbox'
  | 'number'
  | 'text'
  | 'multiline'
  | 'select'
  | 'multiselect'
  | 'nodeRefs'
  | 'stringList'
  | 'json';

export type ColumnPropertySpec = {
  kind: PropertyEditorKind;
  /** Closed option set, when the column has one. */
  options?: readonly string[];
  /**
   * Every separator the committing converter ACCEPTS on input. All four list
   * columns split on `/[,;|]/` (`convertTo*Array` in `table-store.ts`), so this
   * is a set, not a single character — a UI that shows only "," would be lying.
   */
  separators?: readonly string[];
  /**
   * The grammar marks every field optional; these are the ones the compiler's
   * VALIDATORS require (`validate-node-number`, `-node-type`, `-node-name`).
   * See F-notes: grammar and validators disagree here, and the validators win.
   */
  required?: boolean;
  /**
   * Columns whose legal values depend on this one. Used to explain a diagnostic
   * that names a DIFFERENT column than the one being edited, which is otherwise
   * baffling — e.g. setting `richAssetType` to datepicker makes `behaviors`
   * invalid until `disable_input` is added.
   */
  affects?: readonly string[];
  /**
   * The shape of ONE item, where the compiler enforces one.
   *
   * Separate from `separators` because knowing the delimiter is useless if the
   * item itself has a required form — `whatsNext` splits on `|` and then
   * demands `value~destination` per item, and it is the second rule that
   * produces the error. A hint that teaches only the delimiter teaches the
   * half the user could already infer from looking at the cell.
   *
   * These are mirrored from the compiler's parsers (`common/parsers/`), which
   * expose no machine-readable form of them — same situation as the behaviour
   * list, so the same caveat applies: they can drift, and only a reader will
   * notice.
   */
  itemFormat?: string;
  /**
   * Curated one-line help, shown under the field in the node property panel.
   *
   * Distinct from the hint `getFieldHint` derives: that one is generated from
   * `separators` / `itemFormat` and can only ever restate the grammar. This is
   * prose written for the panel — it says what the field is FOR, which the
   * compiler cannot tell us. Where both exist this wins, because a curated line
   * that already names the format ("written as outcome~nodeNumber and separated
   * by pipes") makes the generated one redundant.
   *
   * Only set where there is something non-obvious to say. A hint under every
   * field is noise, and noise is how the useful one gets ignored.
   */
  help?: string;
};

/**
 * Behaviours the compiler ACCEPTS — the publish gate.
 *
 * `validate-behaviors.ts` gates on the compiler's own enum, so anything absent
 * there fails at publish. This is a MIRROR of it, kept honest by
 * `yarn check:behavior-drift` rather than by import (see the note at the top of
 * this file for why importing is not possible here).
 */
export const COMPILER_ACCEPTED_BEHAVIORS: readonly string[] = [
  'multiple_choice',
  'disable_input',
  'xfer_to_agent',
  'inline',
  'out_of_scope',
  'voice_enable_barge_in',
  'is_collapsible',
  // Present in the PUBLISHED 1.11.0 build and deliberately NOT offered — the
  // behaviour was withdrawn. See OFFERED_BEHAVIORS.
  'voice_allow_preempt',
];

/**
 * Behaviours the picker OFFERS — a product decision, deliberately not the
 * compiler's set.
 *
 * These are different questions and conflating them is a bug in both
 * directions:
 *
 * - `out_of_scope` is accepted by the compiler but is a SYSTEM intent, not
 *   something an author picks per node.
 * - `voice_allow_preempt` is accepted by the published compiler build and was
 *   **withdrawn** — the voice runtime pins text non-preemptible on purpose, so
 *   offering it would resurrect a feature that was deliberately killed. The
 *   published enum is a snapshot and lags the source; it cannot be treated as
 *   the list of things worth showing.
 *
 * So the compiler answers "will this publish?" and this answers "should we
 * show it?". `check-behavior-drift.mjs` asserts this stays a SUBSET of what the
 * REAL published compiler accepts, which is the failure that actually matters —
 * offering an author something publish will reject.
 */
export const OFFERED_BEHAVIORS: readonly string[] = [
  'disable_input',
  'inline',
  'multiple_choice',
  'xfer_to_agent',
  'voice_enable_barge_in',
  'is_collapsible',
];

/**
 * Node types, in the case the APP STORES — lower-case.
 *
 * The compiler's `NODE_TYPE` enum is upper-case (`'D'`/`'A'`) and
 * `validate-node-type.ts` accepts either, but the value on a row is the GraphQL
 * enum, whose members are lower-case: `Tenancy_Node_Type_Enum_Ps_Enum.A === 'a'`.
 * The existing editors agree — the panel's select uses `value="d"`/`value="a"`,
 * and `convertTextToColumnValue('Action', 'nodeType')` is asserted to be `'a'`.
 *
 * Mirroring the compiler's casing here would be a real bug rather than a
 * cosmetic one: the option would never match the stored value, and `ps-select`
 * BLANKS a value it cannot find among its rendered options (it sets
 * `this.value = this.selectedOptions[0]?.value ?? ''`), so the field would show
 * empty for every existing node.
 */
export const NODE_TYPE_OPTIONS: readonly string[] = ['d', 'a'];

/** Intents the compiler treats as built-in. */
export const COMPILER_VALID_INTENTS: readonly string[] = ['out_of_scope'];

const LIST_SEPARATORS = [',', ';', '|'] as const;

const SPECS: Partial<Record<string, ColumnPropertySpec>> = {
  // --- Read-only / derived -------------------------------------------------
  order: { kind: 'readonly' },
  id: { kind: 'readonly' },

  // --- Booleans ------------------------------------------------------------
  answerRequired: { kind: 'checkbox' },
  nluDisabled: {
    kind: 'checkbox',
    // An NLU-disabled node may have exactly one child (validate-next-nodes).
    affects: ['nextNodes'],
  },

  // --- Numbers -------------------------------------------------------------
  nodeNumber: {
    kind: 'number',
    required: true,
    help: 'Unique within the solution. Other nodes route to this number.',
  },

  // --- Closed sets ---------------------------------------------------------
  nodeType: {
    kind: 'select',
    options: NODE_TYPE_OPTIONS,
    required: true,
    // `command` is required on an action node (validate-command).
    affects: ['command'],
    help: 'Decision nodes talk to the customer. Action nodes run a command.',
  },
  behaviors: {
    kind: 'multiselect',
    options: OFFERED_BEHAVIORS,
    separators: LIST_SEPARATORS,
    // disable_input is REQUIRED for datepicker/timepicker/file_upload; inline
    // only for webview; is_collapsible only for four asset types.
    affects: ['richAssetType'],
  },

  // --- Lists ---------------------------------------------------------------
  nextNodes: {
    kind: 'nodeRefs',
    separators: LIST_SEPARATORS,
  },
  whatsNext: {
    kind: 'stringList',
    separators: LIST_SEPARATORS,
    // `parse-whats-next.ts` splits on `|` and then requires `value~destination`
    // per item, rejecting anything else with "The whatsNext item is not
    // formatted correctly." The frontend accepts `, ; |` on input and re-joins
    // with `|` (`joinDelimitedField(whatsNext, '|')`), so all three separators
    // are safe to type even though the compiler only ever sees pipes.
    itemFormat: 'value~destination',
    help: 'Routing map, written as outcome~nodeNumber and separated by pipes.',
  },
  output: { kind: 'stringList', separators: LIST_SEPARATORS },
  variables: {
    kind: 'stringList',
    separators: LIST_SEPARATORS,
    // convertToVariablesArray UPPERCASES and is all-or-nothing: one invalid
    // name discards the whole list. Worth surfacing in the editor.
    affects: ['decisionVariable', 'output'],
  },

  // --- JSON ----------------------------------------------------------------
  parameterInput: { kind: 'json' },
  // Free-form event payload the runtime emits for this node. JSON-shaped in
  // practice, and `tableStore.isJsonColumn` already treats it as such, so the
  // panel gets the JSON affordances (order map preserved on commit).
  analyticsEvent: { kind: 'json' },

  // --- Dynamic option sets -------------------------------------------------
  // `options` is deliberately ABSENT on these two. Their legal values are not a
  // constant the compiler can hand us: asset types are filtered per channel and
  // per node (`getAssetTypeList`), and commands are the solution's uploaded
  // scripts plus the dynamic native functions (`buildScriptFileNames`). Both
  // lists live in the store and the panel reads them there, exactly as the grid
  // cell does. Recording `kind: 'select'` here is still worth it — it tells the
  // panel to render a picker rather than a text box.
  richAssetType: {
    kind: 'select',
    // disable_input is REQUIRED for datepicker/timepicker/file_upload; inline
    // only for webview; is_collapsible only for four asset types.
    affects: ['behaviors'],
  },
  command: { kind: 'select' },
  richAssetContent: {
    kind: 'multiline',
    separators: ['|'],
    itemFormat: 'label~nextNode',
    help: 'Pipe-separated options, each written as label~nextNode.',
  },

  // --- Long free text ------------------------------------------------------
  message: {
    kind: 'multiline',
    help: 'What the bot says at this node.',
  },
  description: { kind: 'multiline' },
  utterances: {
    kind: 'multiline',
    help: 'What a customer might say to reach this step. Separate phrases with |.',
  },

  // --- Short free text -----------------------------------------------------
  nodeName: { kind: 'text', required: true },
  nodeInput: {
    kind: 'text',
    // COMMA ONLY — `parse-node-input.ts` does `nodeInput.split(',')`, and the
    // same parser explicitly REJECTS `~`, `|` and `=` inside an item. So this
    // must not advertise the usual `, ; |` set: doing so would walk an author
    // straight into "Characters '~', '|' and '=' are not allowed."
    //
    // Left as `text` rather than `stringList` on purpose — it is NOT in
    // `ARRAY_COLUMNS`, so the commit path stores it as a plain string, and
    // calling it a list here would mislead the next reader.
    separators: [','],
    itemFormat: 'variable:nodeNumber (optionally *payload)',
  },
  decisionVariable: { kind: 'text' },
  skillTag: { kind: 'text' },
  cssClassname: { kind: 'text' },
  groupName: { kind: 'text' },
};

/**
 * The editing spec for a column.
 *
 * Falls back to `multiline` for anything unlisted, which is what the panel does
 * today for every column outside `getColumnEditKind`'s five named ones. Kept as
 * a fallback rather than an exhaustive map so an unknown or newly added column
 * degrades to a usable editor instead of rendering nothing.
 */
export const getColumnPropertySpec = (
  columnId: keyof TableRow | NodeColumns | string
): ColumnPropertySpec =>
  // `hasOwn` and not a bare `SPECS[columnId] ?? …`: the lookup key is a plain
  // string, so a bare index resolves `Object.prototype` members —
  // `getColumnPropertySpec('constructor')` would return a FUNCTION (not
  // nullish, so the `??` never fires), whose `.kind` is `undefined`, and the
  // panel would render nothing for it instead of the documented `multiline`
  // fallback. Same class of bug as the pasted-text prototype-key fix (82c15bb).
  (Object.prototype.hasOwnProperty.call(SPECS, columnId)
    ? SPECS[columnId]
    : undefined) ?? { kind: 'multiline' };
